import React, { useCallback, useEffect, useState } from 'react';
import { useMytaverse } from '@providers/MytaverseProvider';
import ParticipantsService from '@services/ParticipantsService';
import PolicyModalView from './PolicyModalView';
import { IUser } from '@interfaces/user';
import { usePolicyState } from '@core/myta/components/PolicyModal/context';
import { AnalyticsType } from '@interfaces/analytics';

const PolicyModal = () => {
  const { user, setUser, logoutUser, trackAnalytics, appSettings } = useMytaverse();
  const blackListDomains = [
    'zhacity.mytaverse.com',
    'metrotopia.mytaverse.com',
  ];
  const [isEnd, setIsEnd] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { open, setOpen } = usePolicyState();

  useEffect(() => {
    const isNotInBlackList = blackListDomains.some(
      (domain) => domain === window.location.hostname,
    );

    if (user && !user.isTermsAccepted && !isNotInBlackList) {
      setOpen(true);
      trackAnalytics(AnalyticsType.ViewPrivacyPolicy, { ref: 'welcome' });
    } else {
      setOpen(false);
    }
  }, [user]);

  const handleClose = useCallback(() => {
    if (!user?.isTermsAccepted) {
      logoutUser();
    } else {
      setOpen(false);
    }
  }, [logoutUser, user?.isTermsAccepted]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;

    const buffer = 30;

    if (
      target.scrollHeight - target.scrollTop <=
      target.clientHeight + buffer
    ) {
      setIsEnd(true);
    }
  }, []);

  const handleAccept = useCallback(async () => {
    if (!isEnd || !user) {
      return;
    }

    try {
      setIsProcessing(true);
      await ParticipantsService.updateCurrentParticipantProfileTC({
        isTermsAccepted: true,
      });
      trackAnalytics(AnalyticsType.AcceptTerms);

      setUser((prevState: IUser) => ({
        ...prevState,
        isTermsAccepted: true,
      }));

      setOpen(false);
    } catch (e) {
      console.error('Error updating profile:', e);
    } finally {
      setIsProcessing(false);
    }
  }, [user, isEnd, setOpen, trackAnalytics]);

  if (!open || !appSettings?.termsPDFUrl) {
    return null;
  }

  return (
    <PolicyModalView
      file={appSettings.termsPDFUrl}
      open={open}
      isRead={isEnd}
      disabled={!isEnd || isProcessing}
      handleScroll={handleScroll}
      onClose={handleClose}
      onClick={handleAccept}
      isAccepted={!!user?.isTermsAccepted}
    />
  );
};

export default PolicyModal;
